import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'APP/Hooks/redux'
import { View } from 'react-native'
import styled from 'styled-components/native'

import Config from 'APP/Config'

import {
  TabScreens,
  shouldRenderComponent,
  filterServiceGroupsAndServicesForEligibility,
  useAppComponentMapper,
} from 'APP/Lib/ContentHelpers'
import { selectExternalServices } from 'APP/Redux/PatientRedux'
import { contentSelectors } from 'APP/Store/Content'
import Analytics from 'APP/Services/Analytics'
import sprigClient from 'APP/Services/Sprig'

import HeaderTabs from 'APP/Converse/HeaderTabs'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueBranding from 'APP/Components/DialogueBranding'
import { HelpButtonComponent } from 'APP/Components/HelpButtonComponent'

import Styles from './style'

const BrandingBanner = styled.View`
  background-color: ${({ theme }) => theme.colors.elementsBg};
  border-color: ${({ theme }) => theme.colors.elementsBorder};
  border-top-width: 1px;
`

const CmsAppTabScreen = (props) => {
  const { navigation, route } = props
  const { tabName } = { ...route?.params, ...props }
  const name = route.name

  const screen = useSelector((state) =>
    state?.content?.layout?.data?.screens?.find((screen) => screen?.mobile_scene_key === name)
  )
  const patient = useSelector((state) => state?.patient)
  const history = useSelector((state) => state?.history)
  const eligibleServices = patient?.profile?.eligibleServices
  const orderedEpisodes = history?.orderedEpisodes
  const carePlans = useSelector((state) => state?.history?.completedCarePlans)
  const unreadMessages = useSelector((state) => state.history?.hasUnreadChannels)
  const externalServices = useSelector(selectExternalServices)
  const allServiceGroups = useSelector(contentSelectors.selectAllServiceGroups)

  const [initialTabSet, setInitialTabSet] = useState(null)

  const mapAppComponent = useAppComponentMapper()

  const tabs = useMemo(
    () =>
      screen?.tabs?.reduce((filteredTabs, tab) => {
        let shouldRenderTab = shouldRenderComponent(tab.service_filter, eligibleServices)
        let extraProps = {}

        if (tab.name === TabScreens.chat.getCare) {
          shouldRenderTab =
            filterServiceGroupsAndServicesForEligibility(
              allServiceGroups,
              eligibleServices,
              externalServices
            ).length > 0
        } else if (tab.name === TabScreens.chat.inbox) {
          shouldRenderTab = shouldRenderTab || orderedEpisodes?.length > 0
          extraProps = {
            newContent: unreadMessages,
          }
        } else if (tab.name === TabScreens.chat.carePlans) {
          shouldRenderTab = shouldRenderTab || carePlans?.length > 0
        }

        if (shouldRenderTab) {
          filteredTabs.push({
            ...tab,
            ReactComponent: mapAppComponent(tab.component),
            extraProps,
          })
        }
        return filteredTabs
      }, []) ?? [],
    [
      screen,
      unreadMessages,
      allServiceGroups,
      eligibleServices,
      externalServices,
      orderedEpisodes,
      carePlans,
    ]
  )

  const activeTab = tabs.find((tab) => tab.name === tabName) || tabs[0]

  const { ReactComponent, component } = activeTab ?? {}

  const goToTabByName = useCallback(
    (name) => {
      if (name) navigation.navigate(screen?.mobile_scene_key, { tabName: name })
    },
    [navigation, screen?.mobile_scene_key]
  )

  useLayoutEffect(() => {
    if (name !== 'mindTab') return
    navigation.setOptions({
      headerRight: () => <HelpButtonComponent />,
    })
  })

  // Track tab changes as screen views
  useEffect(() => {
    if (tabName) {
      Analytics.trackScreen(tabName)
      sprigClient.track(`SCREEN_VIEW_${tabName}`)
    }
  }, [tabName])

  // Set tab on initial load
  useEffect(() => {
    if (!tabName && tabs.length && !initialTabSet) {
      goToTabByName(tabs[0].name)
      setInitialTabSet(tabs[0].name)
    }
  }, [tabs, tabName, initialTabSet, goToTabByName, setInitialTabSet])

  return (
    <View style={{ flex: 1 }}>
      <HeaderTabs
        tabs={tabs}
        activeTabName={tabName}
        onPress={goToTabByName}
        testID="TAB_SCREEN_TABS"
      />
      <View style={Styles.container}>
        <NoticeBarContainer containerStyle={Styles.noticeContainer}>
          <View style={Styles.viewContainer} testID={component?.mobile_component_name}>
            {ReactComponent && (
              <ReactComponent
                key={component?.id}
                goToTabByName={goToTabByName}
                contentId={component?.data?.id}
              />
            )}
          </View>
          {/* This is a hack. See https://dialoguemd.atlassian.net/browse/DIA-84754
          for reasoning*/}
          {Config.BRAND_ID === 'sunlife' && Config.BRAND_VERSION === 'v2' && (
            <BrandingBanner testID="BRANDING_BANNER">
              <DialogueBranding />
            </BrandingBanner>
          )}
        </NoticeBarContainer>
      </View>
    </View>
  )
}

export default CmsAppTabScreen
